import { FormattedMessage, useIntl } from 'react-intl';

import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { SMALL_SCREEN_BOTTOM_SHEET_PARAM } from '@xing-com/crate-jobs-constants';
import { FILTER_TYPENAME_TO_NAME } from '@xing-com/crate-jobs-domain-search-maps';
import type {
  FilterName,
  FilterResult,
} from '@xing-com/crate-jobs-domain-search-types';
import type { ExtractedFiltersEnabledFragment } from '@xing-com/crate-jobs-graphql';
import { useSearchState } from '@xing-com/crate-jobs-hooks';
import { Tag } from '@xing-com/tag';

import { useSearch } from '../../../hooks';

const hasExtractedFilters = (
  extractedFilters: (ExtractedFiltersEnabledFragment | null)[],
  filter: 'salary' | FilterResult
): boolean => {
  return extractedFilters.some((extractedFilter) => {
    if (filter === 'salary') {
      return (
        extractedFilter?.__typename === 'JobFilterSalary' &&
        (typeof extractedFilter.min === 'number' ||
          typeof extractedFilter.max === 'number')
      );
    }

    return (
      extractedFilter?.__typename &&
      'entityId' in extractedFilter &&
      FILTER_TYPENAME_TO_NAME[extractedFilter.__typename] === filter?.name
    );
  });
};

type Props = {
  filter: FilterResult | 'salary';
  trackClick: (name: string) => void;
};

export const FilterBarItem: React.FC<Props> = ({ filter, trackClick }) => {
  const { formatMessage } = useIntl();
  const { searchQuery } = useSearchState();
  const { extractedFilters } = useSearch();
  const { setSearchParameter } = useSearchParameter();

  const isExtractedFilterSelected = hasExtractedFilters(
    extractedFilters,
    filter
  );

  const handleOnFilterItemClick = (view: FilterName | 'salary') => () => {
    setSearchParameter(SMALL_SCREEN_BOTTOM_SHEET_PARAM, view);
    trackClick(view);
  };

  if (!filter) {
    return null;
  }

  if (filter === 'salary') {
    const ariaLabel = formatMessage(
      { id: 'JOBS_SEARCH_TOP_FILTER_TITLE_A11Y' },
      { variable_1: formatMessage({ id: 'JOBS_SEARCH_FILTER_SALARY' }) }
    );

    return (
      <Tag
        hideBorder
        selected={!!searchQuery.filter?.salary || isExtractedFilterSelected}
        onClick={handleOnFilterItemClick('salary')}
        data-testid="filter-bar-filter"
        aria-label={ariaLabel}
      >
        <FormattedMessage id="JOBS_SEARCH_FILTER_SALARY" />
      </Tag>
    );
  }

  const isSelected =
    searchQuery.filter?.[filter.name]?.id.length || isExtractedFilterSelected;

  const ariaLabel = formatMessage(
    { id: 'JOBS_SEARCH_TOP_FILTER_TITLE_A11Y' },
    { variable_1: formatMessage({ id: filter.titleKey }) }
  );

  return (
    <Tag
      hideBorder
      selected={!!isSelected}
      onClick={handleOnFilterItemClick(filter.name)}
      data-testid="filter-bar-filter"
      disabled={!filter.options}
      aria-label={ariaLabel}
    >
      <FormattedMessage id={filter.titleKey} />
    </Tag>
  );
};
